import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MaskedLine, FadeUp } from "./MaskedReveal";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Cessna 408 SkyCourier Training",
  "King Air Training",
  "Government & Special Mission Programs",
  "Aircraft Management",
  "Daily Contract Pilot",
  "Worldwide Aircraft Delivery",
  "Owner-Flown Mentoring",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: services[0],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-6">
            <div className="font-mono-label text-white/50 mb-6">
              <span className="text-[#FF5A00]">◉</span> Contact · 007
            </div>
            <h2 className="font-display uppercase leading-[0.84] tracking-tighter text-[16vw] md:text-[10vw] lg:text-[9vw]">
              <MaskedLine>Let's</MaskedLine>
              <MaskedLine delay={0.12}>Fly.</MaskedLine>
            </h2>

            <FadeUp delay={0.2}>
              <p className="mt-10 text-white/60 max-w-md text-base md:text-lg leading-relaxed">
                Training seat, contract pilot, delivery ferry, or a full
                management conversation &mdash; write us. Every inquiry is read by a
                pilot, not a bot.
              </p>
            </FadeUp>

            <div className="mt-14 space-y-6 font-mono-label text-white/60">
              <div>
                <div className="text-white/40 mb-1">Operations</div>
                <div className="text-white text-[13px] tracking-[0.24em]">
                  info@elelyonair.com
                </div>
              </div>
              <div>
                <div className="text-white/40 mb-1">Base</div>
                <div className="text-white text-[13px] tracking-[0.24em]">
                  North America · Worldwide Delivery
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="md:col-span-6 md:col-start-7 border border-white/10 bg-[#0A0A0C] p-6 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                testid="contact-input-name"
                required
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                testid="contact-input-email"
                required
              />
              <Field
                label="Phone (optional)"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                testid="contact-input-phone"
              />
              <div className="flex flex-col gap-2">
                <label className="font-mono-label text-white/50">
                  Service
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm transition-colors duration-500"
                  data-testid="contact-input-service"
                >
                  {services.map((s) => (
                    <option
                      key={s}
                      value={s}
                      className="bg-[#0A0A0C] text-white"
                    >
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <label className="font-mono-label text-white/50">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                placeholder="Tell us about your aircraft, mission, or training goals."
                className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm placeholder:text-white/25 resize-none transition-colors duration-500"
                data-testid="contact-input-message"
                required
              />
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.98 }}
              className="btn-outline btn-solid mt-10 w-full justify-center disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? "Transmitting…" : sent ? "Sent — Send Another?" : "Send Inquiry"}
              <span aria-hidden>→</span>
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testid }) => (
  <div className="flex flex-col gap-2">
    <label className="font-mono-label text-white/50">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete="off"
      data-testid={testid}
      className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm transition-colors duration-500"
    />
  </div>
);
